import { useMemo } from "react";
import { useMutation } from "@apollo/client";
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Heading,
  Text,
} from "@chakra-ui/react";
import { useController, useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { lowerFirst } from "lodash";
import { DocumentType, gql } from "__generated__/gql";
import {
  makeClearCachedType,
  makeClearListFieldCache,
} from "utils/apollo/cacheHelpers";
import { useActiveBrandIdContext } from "utils/apollo/hooks/brand";
import { useQuery } from "utils/apollo/hooks";
import { ADMIN_CONTEXT } from "utils/apollo/hooks/admin";
import { getDataIsByIdField } from "utils/apollo/misc";
import Form from "components/Shared/Forms/Form";
import createFormFieldComponents from "components/Shared/Forms/FormField";
import SwitchInput from "components/Shared/Forms/SwitchInput";
import useCreateValidSubmitHandler from "components/Shared/Forms/useCreateValidSubmitHandler";
import { RenderContentProps } from "components/Shared/MergeModal/MergeModalButton";
import { GRID_LAYOUT_PROPS } from "components/Shared/LabeledValueTable";
import { APP_NAME } from "utils/config";
import { OrderingPlatformFilter } from "__generated__/gql/graphql";
import { EmbeddedAdminManageOrderingPlatformsTable } from "components/Admin/OrderingPlatforms/Manage/Table";
import type { AppliedFilter } from "components/Shared/FilterEditor/utils";
import { INCLUDE_CUSTOM_FILTER_NAME } from "components/Shared/IncludeCustomFilter";
import { defaultRenderContentInline } from "components/Shared/Forms/FormFieldWrapperInline";
import { ORDERING_PLATFORM_TYPE_FILTER_ID } from "./filterDefinitions";
import OrderingPlatformDetails from "./Details";
import OrderingPlatformDetailsDistributionCentersField from "./DetailsDistributionCentersField";
import OrderingPlatformsTable from ".";

const query = gql(/* GraphQL */ `
  query AdminSelectedOrderingPlatformMerge($orderingPlatformId: UUID!) {
    orderingPlatform(id: $orderingPlatformId) {
      id
      name
      forBrandId
      orderingPlatformType
      directRetailerId
      isParent
      parentId
      ...OrderingPlatformDetails
      ...OrderingPlatformDetailsDistributionCenters
    }
  }
`);

const mutation = gql(/* GraphQL */ `
  mutation AdminMergeOrderingPlatform(
    $fromId: UUID!
    $toId: UUID!
    $shouldMergeDistributionCenters: Boolean = false
  ) {
    mergeOrderingPlatforms(
      input: {
        fromOrderingPlatformId: $fromId
        toOrderingPlatformId: $toId
        shouldMergeDistributionCenters: $shouldMergeDistributionCenters
      }
    ) {
      orderingPlatform {
        id
      }
    }
  }
`);

type FormFields = {
  toId: UUID;
  shouldMergeDistributionCenters: boolean;
};

const schema: yup.ObjectSchema<FormFields> = yup.object().shape({
  toId: yup
    .string()
    .uuid()
    .nullable()
    .transform((v: UUID | null | undefined) => v || null)
    // eslint-disable-next-line no-template-curly-in-string
    .required("Please select a ${label} with which to merge this customer.")
    .label("Destination Customer"),
  shouldMergeDistributionCenters: yup
    .bool()
    .default(false)
    .label("Merge Distribution Centers?"),
});

const { ControlledFormField } = createFormFieldComponents<FormFields>({
  schema,
});

type Props = RenderContentProps & {
  isAdmin?: boolean;
  onCompleted: (data: DocumentType<typeof mutation>) => void;
  orderingPlatformId: UUID;
};

export default function MergeOrderingPlatform({
  formId,
  isAdmin,
  onClose,
  onCompleted,
  orderingPlatformId,
  setIsLoading,
}: Props) {
  const formContext = useForm<FormFields>({
    resolver: yupResolver(schema),
    defaultValues: { toId: "" },
  });

  const brandContext = useActiveBrandIdContext();

  const context = isAdmin ? ADMIN_CONTEXT : brandContext;

  const { data, loading } = useQuery(query, {
    skip: !context,
    context,
    variables: { orderingPlatformId },
    returnPartialData: true,
    nextFetchPolicy: "standby",
  });

  const [performMutation] = useMutation(mutation, {
    context,
    update: (cache) => {
      const clearCachedType = makeClearCachedType(cache);
      ["OrderingPlatform", "DistributionCenter"].forEach((baseType) => {
        ["", "Brand", "SkuBrand"].forEach((prefix) => {
          const typeName = `${prefix}${baseType}`;
          cache.modify({
            fields: makeClearListFieldCache(`${lowerFirst(typeName)}s`),
          });
          clearCachedType(typeName);
        });
      });
      cache.gc();
    },
    onQueryUpdated: (observableQuery) => {
      const queryData = observableQuery.getLastResult()?.data as {
        [key: string]: unknown;
      };
      if (
        getDataIsByIdField(queryData, "orderingPlatform", orderingPlatformId)
      ) {
        return false;
      }
    },
    onCompleted: (completedData) => {
      onClose();
      onCompleted(completedData);
    },
  });

  const [handleValidSubmit] = useCreateValidSubmitHandler(
    (formData: FormFields) => {
      const { toId, shouldMergeDistributionCenters } = formData;
      setIsLoading(true);
      return performMutation({
        variables: {
          fromId: orderingPlatformId,
          toId,
          shouldMergeDistributionCenters,
        },
      }).finally(() => setIsLoading(false));
    },
    { successMessage: "Customer merged successfully" },
  );

  const toIdController = useController({
    control: formContext.control,
    name: "toId",
  });

  const { orderingPlatform } = data || {};

  const orderingPlatformFilters = useMemo<OrderingPlatformFilter>(() => {
    const baseFilter: OrderingPlatformFilter = {
      id: { notEqualTo: orderingPlatformId },
    };

    if (isAdmin) {
      if (orderingPlatform?.forBrandId) {
        baseFilter.or = [
          { forBrandId: { equalTo: orderingPlatform.forBrandId } },
          { forBrandId: { isNull: true } },
        ];
      } else {
        baseFilter.forBrandId = { isNull: true };
      }
    } else {
      baseFilter.forBrandId = { equalTo: orderingPlatform?.forBrandId };
    }

    baseFilter.directRetailerId = orderingPlatform?.directRetailerId
      ? {
          equalTo: orderingPlatform.directRetailerId,
        }
      : { isNull: true };

    if (typeof orderingPlatform?.isParent === "boolean") {
      baseFilter.isParent = { equalTo: orderingPlatform.isParent };
    }

    baseFilter.parentId = orderingPlatform?.parentId
      ? {
          equalTo: orderingPlatform.parentId,
        }
      : { isNull: true };

    return baseFilter;
  }, [isAdmin, orderingPlatformId, orderingPlatform]);

  const defaultFilters = useMemo(() => {
    const result: AppliedFilter[] = [];

    if (orderingPlatform?.orderingPlatformType) {
      result.push({
        filter: ORDERING_PLATFORM_TYPE_FILTER_ID,
        value: [orderingPlatform.orderingPlatformType],
        variant: "in",
      });
    }

    if (isAdmin) {
      result.push({
        filter: INCLUDE_CUSTOM_FILTER_NAME,
        value: !!orderingPlatform?.forBrandId,
        variant: "equalTo",
      });
    }

    if (!result.length) return;

    return result;
  }, [isAdmin, orderingPlatform]);

  return (
    <>
      <Box fontSize="sm">
        <Text>
          This will attempt to merge all of the attributes and associations of
          this selected customer into another customer. Attributes that have
          already been set on the destination customer will take precedence.
          This action cannot be undone and should be used with{" "}
          <strong>extreme caution</strong> as customer merges cause numerous
          downstream changes.
        </Text>
        <Text my={3}>
          In some cases, when both the selected customer and the destination
          customer have been added to{" "}
          {isAdmin ? `${APP_NAME} accounts` : "this brand"},{" "}
          <strong>the merge may fail</strong>. In this case, please review each
          usage of the customer
          {isAdmin ? " in each brand account using it" : ""} and delete one if
          possible.
        </Text>
        <Text my={3}>
          Once merged, this currently selected customer will be removed:
        </Text>

        <OrderingPlatformDetails
          data={data?.orderingPlatform}
          isAdmin={isAdmin}
          isLoading={loading}
          isNameIncluded
          {...GRID_LAYOUT_PROPS}
        >
          <OrderingPlatformDetailsDistributionCentersField
            orderingPlatform={data?.orderingPlatform}
          />
        </OrderingPlatformDetails>

        {isAdmin && orderingPlatform?.forBrandId && (
          <Alert my={10}>
            <AlertIcon />
            <AlertDescription lineHeight="normal">
              This is a <strong>custom</strong> customer. If you choose to merge
              it into a <strong>default</strong> customer, the brand that
              created it will no longer be able to edit its details (though it
              will still be able to edit its placement details, which is usually
              all that is needed).
            </AlertDescription>
          </Alert>
        )}
      </Box>

      <Heading as="h6" size="xs" color="magenta.veryDark" mt={6}>
        Select Destination Customer
      </Heading>
      <Text mb={4}>
        Select the customer into which you would like to merge this customer:
      </Text>

      {isAdmin ? (
        <EmbeddedAdminManageOrderingPlatformsTable
          defaultFilters={defaultFilters}
          defaultPageSize={10}
          filter={orderingPlatformFilters}
          nextFetchPolicy="standby"
          selectedOrderingPlatformId={toIdController.field.value}
          onSelectedOrderingPlatformIdChange={(id) =>
            toIdController.field.onChange(id)
          }
        />
      ) : (
        <OrderingPlatformsTable
          defaultFilters={defaultFilters}
          filter={orderingPlatformFilters}
          nextFetchPolicy="standby"
          selected={toIdController.field.value}
          onSelectedChange={(id) => toIdController.field.onChange(id)}
        />
      )}

      <Form
        formContext={formContext}
        onValidSubmit={handleValidSubmit}
        id={formId}
      >
        <ControlledFormField
          name="toId"
          label={null}
          renderInput={({ field }) => <input type="hidden" {...field} />}
        />

        <ControlledFormField
          name="shouldMergeDistributionCenters"
          helpText={
            <>
              If enabled, distribution centers with the same name will attempt
              to be merged with those in the destination customer.
            </>
          }
          renderContent={defaultRenderContentInline}
          renderInput={({ field }) => <SwitchInput {...field} />}
          labelProps={{ textAlign: "inherit", mx: 0 }}
          display="flex"
          alignItems="center"
          justifyContent="flex-end"
          sx={{
            ".label-parts": { maxWidth: "22em" },
            ".input-parts": { flex: "0 0 auto" },
          }}
          gap={4}
          textAlign="right"
        />
      </Form>
    </>
  );
}
